import React, { useMemo } from "react";
import moment from "moment";
import { FiInbox } from "react-icons/fi";
import { FaArrowUp } from "react-icons/fa";
import AuthInput from "../../../components/Input/AuthInput";
import CustomPagination from "../../../components/TablePagination/TablePagination";
import RowActionsMenu from "../../../components/RowActionsMenu/RowActionsMenu";
import { formatDateTime } from "../../../utils/dateTime";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatPaymentDate = (row) => {
  const unix = Number(row?.date);
  if (Number.isFinite(unix) && unix > 0) return formatDateTime(unix);
  if (row?.f_date) return formatDateTime(row.f_date);
  return "—";
};

const getAccountName = (row) =>
  row?.account_name || row?.accountId?.account_name || "CASH";

const buildSubtitle = (row) => {
  const parts = [];
  if (row.company_name) parts.push(row.company_name);
  if (row.invoice_no) parts.push(`Inv ${row.invoice_no}`);
  if (row.desc?.trim()) parts.push(row.desc.trim());
  return parts.join(" · ") || "Company payment";
};

const ReportCompanyPaymentCard = ({
  payment,
  onEditPayment,
  onDeletePayment,
}) => {
  const amount = Number(payment?.amount || 0);
  const subtitle = buildSubtitle(payment);
  const account = getAccountName(payment);
  const canManage =
    typeof onEditPayment === "function" ||
    typeof onDeletePayment === "function";

  return (
    <article className="rounded-2xl bg-card border border-line shadow-soft overflow-hidden font-quicksand">
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-dangerSoft">
          <FaArrowUp className="text-danger text-base" aria-hidden />
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          {amount > 0 && (
            <p className="text-xl font-extrabold tabular-nums leading-none text-danger">
              Rs {fmtRs(amount)}
              <span className="text-xs font-semibold ml-2 text-danger">DR</span>
            </p>
          )}
          <p className="text-sm text-ink mt-2 leading-snug line-clamp-3">
            {subtitle}
          </p>
        </div>

        {canManage && (
          <div className="shrink-0 -mt-1 -mr-1">
            <RowActionsMenu
              row={payment}
              onEdit={onEditPayment}
              onDelete={onDeletePayment}
            />
          </div>
        )}
      </div>

      <div className="mx-4 border-t border-line" />

      <div className="px-4 py-3 flex items-center justify-between gap-3 text-sm text-muted">
        <span className="shrink-0">{formatPaymentDate(payment)}</span>
        <span className="tabular-nums font-medium text-right truncate">
          Account: {account}
        </span>
      </div>
    </article>
  );
};

const ReportCompanyPaymentCardList = ({
  payments = [],
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onRowsPerPageChange,
  searchText,
  setSearchText,
  emptyMessage = "No company cash payments found for this date range.",
  paginate = true,
  showSearch = true,
  onEditPayment,
  onDeletePayment,
}) => {
  const activePayments = payments.filter((row) => row.isDeleted !== true);

  const filtered = activePayments.filter((row) => {
    if (!searchText?.trim()) return true;
    const q = searchText.toLowerCase();
    return (
      String(row.company_name || "").toLowerCase().includes(q) ||
      String(row.invoice_no || "").toLowerCase().includes(q) ||
      String(row.desc || "").toLowerCase().includes(q) ||
      getAccountName(row).toLowerCase().includes(q) ||
      String(row.f_date || "").includes(q)
    );
  });

  const sortedFiltered = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const dateDiff = Number(a.date || 0) - Number(b.date || 0);
        if (dateDiff !== 0) return dateDiff;
        return String(a._id).localeCompare(String(b._id));
      }),
    [filtered]
  );

  const pageRows = paginate
    ? sortedFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedFiltered;

  if (!activePayments.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
        <div className="w-16 h-16 rounded-2xl bg-[#155f60]/10 flex items-center justify-center mb-4">
          <FiInbox className="text-[#155f60]" size={28} />
        </div>
        <p className="font-bold text-ink text-lg">No company payments</p>
        <p className="text-sm text-muted mt-1 text-center max-w-xs">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-3">
      {showSearch && setSearchText && (
        <AuthInput
          Value={searchText}
          setValue={setSearchText}
          placeholder="Search company, invoice or description…"
        />
      )}

      {filtered.length === 0 ? (
        <div className="w-full text-center text-muted py-10 text-sm rounded-xl border border-dashed border-line bg-subtle">
          No matches for your search.
        </div>
      ) : (
        <div className="space-y-3 rounded-2xl bg-[#F5F5F5] p-3 font-quicksand">
          {pageRows.map((payment) => (
            <ReportCompanyPaymentCard
              key={payment._id || payment.id}
              payment={payment}
              onEditPayment={onEditPayment}
              onDeletePayment={onDeletePayment}
            />
          ))}
        </div>
      )}

      {paginate && onPageChange && onRowsPerPageChange && (
        <CustomPagination
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          RowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </div>
  );
};

export default ReportCompanyPaymentCardList;
